import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, HelpCircle, ArrowLeft, Calendar, ExternalLink, Loader, LinkIcon, ExternalLinkIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import SEOHead from './SEOHead';
import MemoryPanel from './MemoryPanel';
import ReasoningFlow from './ReasoningFlow';
import { generateSEOData } from '../utils/seoUtils';
import './ReportDetail.css';

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      try {
        const response = await axios.get(`/api/reports/${id}`);
        // console.log('Report data:', response.data);
        // console.log('Visualization type:', typeof response.data.visualization);
        // console.log('Visualization content:', response.data.visualization);
        setReport(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const getResultIcon = (result) => {
    switch (result) {
      case 'True':
        return <CheckCircle className="result-icon true" />;
      case 'False':
        return <XCircle className="result-icon false" />;
      case 'Partially True':
        return <AlertCircle className="result-icon partial" />;
      // Distinct from Partially True: nothing was disproved, the evidence just
      // was not there, so it gets a neutral treatment rather than a warning.
      case 'Unverified':
        return <HelpCircle className="result-icon unverified" />;
      default:
        return null;
    }
  };

  const getResultClass = (result) => {
    switch (result) {
      case 'True':
        return 'true';
      case 'False':
        return 'false';
      case 'Partially True':
        return 'partial';
      case 'Unverified':
        return 'unverified';
      default:
        return '';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Normalise escape sequences that survive JSON transport.
  //
  // Deliberately does not touch table pipes. An earlier version padded pipes
  // with spaces and collapsed `|\s+|` to `| |`; because `\s` matches newlines,
  // the `|` ending one row and the `|` starting the next were merged, folding
  // an entire table onto a single line. The padding also broke the
  // `|---|---|` delimiter row, so GFM stopped recognising tables altogether.
  const processMarkdownText = (text) => {
    if (!text || typeof text !== 'string') return '';

    return text
      .replace(/\\r\\n/g, '\n')        // literal \r\n pairs first
      .replace(/\\n/g, '\n')           // literal \n -> newline
      .replace(/\\t/g, '\t')           // literal \t -> tab
      .replace(/\\"/g, '"')            // literal \" -> quote
      .replace(/\\r/g, '\n')           // stray literal \r -> newline
      .replace(/\r\n?/g, '\n')         // normalise real CRLF/CR
      .trim();
  };

  // Filter out Visualization Summary section from markdown content
  const filterVisualizationSummary = (text) => {    if (!text || typeof text !== 'string') return '';
    
    // Remove Visualization Summary section and its content
    // This handles various heading formats (# ## ###) and both English and other languages
    let filtered = text.replace(
      /^#{1,6}\s*(Visualization\s+Summary|可视化摘要|视觉化总结)[\s\S]*?(?=^#{1,6}\s|\n\n---|\n\n\*\*\*|$)/gim, 
      ''
    );
    
    // Clean up any extra newlines that might be left
    filtered = filtered.replace(/\n\n\n+/g, '\n\n').trim();
    
    return filtered;
  };

  // parseVisualizationData removed (unused)

  // Tables in these reports come in two shapes: narrow label/value tables such
  // as the 5W1H breakdown, and wide ones such as Claim-by-Claim Verification.
  // Tagging by column count lets CSS pick the right column strategy without
  // matching on heading text, which the model is free to reword.
  const markdownComponents = useMemo(
    () => ({
      table: ({ node, children, ...props }) => {
        const headerRow = node?.children?.find((c) => c.tagName === 'thead')
          ?.children?.find((r) => r.tagName === 'tr');
        const columns = headerRow?.children?.filter((c) => c.tagName === 'th').length || 0;
        return (
          <table className={columns >= 3 ? 'wide-table' : undefined} {...props}>
            {children}
          </table>
        );
      },
    }),
    []
  );

  // Enhanced source links display
  const renderSourceLinks = () => {
    if (!report.source_links || report.source_links.length === 0) {
      return null;
    }

    return (
      <motion.div 
        className="source-links-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h3 className="section-title">
          <LinkIcon size={24} />
          Original News Sources
        </h3>
        <div className="source-links-grid">
          {report.source_links.map((source, index) => (
            <motion.div
              key={index}
              className="source-link-card"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="source-header">
                <ExternalLinkIcon size={16} />
                <h4>{source.title || 'News Source'}</h4>
              </div>
              <p className="source-snippet">{source.snippet || 'No description available'}</p>
              {source.url && (
                <a 
                  href={source.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="source-url"
                >
                  Visit Source →
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="report-detail">
        <div className="container">
          <div className="loading-container">
            <Loader className="spinner large" />
            <p>Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-detail">
        <div className="container">
          <div className="error-container">
            <XCircle className="error-icon" />
            <h2>Error Loading Report</h2>
            <p>{error}</p>
            <button className="button-primary" onClick={() => navigate('/reports')}>
              Back to Reports
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="report-detail">
        <div className="container">
          <div className="error-container">
            <XCircle className="error-icon" />
            <h2>Report Not Found</h2>
            <p>The requested report could not be found.</p>
            <button className="button-primary" onClick={() => navigate('/reports')}>
              Back to Reports
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Generate SEO data
  const seoData = generateSEOData(report);

  const judge = report.judge_json || {};
  const confidencePercent =
    typeof judge.confidence === 'number'
      ? Math.round(Math.max(0, Math.min(1, judge.confidence)) * 100)
      : null;
  const counts = report.verification?.counts || judge.counts || null;
  const verifications = report.verification?.verifications || [];

  const statusClass = (status) => {
    if (status === 'supported') return 'supported';
    if (status === 'contradicted') return 'contradicted';
    return 'unverified';
  };

  return (
    <div className="report-detail">
      {/* SEO Meta Tags */}
      <SEOHead {...seoData} />
      
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="report-header">
            <button 
              className="back-button"
              onClick={() => navigate('/reports')}
            >
              <ArrowLeft size={20} />
              Back to Reports
            </button>

            <div className="report-meta">
              <Calendar size={16} />
              <span>{formatDate(report.created_at)}</span>
            </div>
          </div>

          <motion.div 
            className="report-title-section glass-effect"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">{report.title}</h1>
            
            <div className="verdict-row">
              <div className={`authenticity-badge ${getResultClass(report.result)}`}>
                {getResultIcon(report.result)}
                <span className="badge-text">{report.result}</span>
              </div>

              {confidencePercent !== null && (
                <div className="confidence-meter">
                  <div className="confidence-head">
                    <span>Confidence</span>
                    <strong>{confidencePercent}%</strong>
                  </div>
                  <div className="confidence-track">
                    <div
                      className={`confidence-fill ${getResultClass(report.result)}`}
                      style={{ width: `${confidencePercent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {counts && (
              <div className="claim-tally">
                <span className="tally-item supported">{counts.core_supported || 0} supported</span>
                <span className="tally-item contradicted">{counts.core_contradicted || 0} contradicted</span>
                <span className="tally-item unverified">{counts.core_unverified || 0} unverified</span>
                <span className="tally-label">core claims</span>
              </div>
            )}
          </motion.div>

          <div className="report-content">
            <motion.div 
              className="original-content glass-effect"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Original News Content</h3>
              <div className="content-text">
                {report.content}
              </div>
            </motion.div>

            <motion.div 
              className="judgment-summary glass-effect"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3>Quick Summary</h3>
              <div className="summary-content">
                {/* <div className="summary-result">
                  {getResultIcon(report.judge_json.result)}
                  <div>
                    <div className="result-text">{report.judge_json.result}</div>
                  </div>
                </div> */}
                
                {/* Summary Markdown Section */}
                {report.visualization?.summary_markdown && (
                  <div className="summary-markdown-section">
                    <h4>Summary Analysis:</h4>
                    <div className="markdown-content summary-markdown">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        skipHtml={false}
                        allowedElements={undefined}
                      >
                        {processMarkdownText(report.visualization.summary_markdown)}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Interactive reasoning DAG built from the verified data.
                    Legacy reports carry `reasoning_html` instead. */}
                {report.visualization?.graph?.nodes?.length ? (
                  <div className="reasoning-section">
                    <h4>Reasoning Chain</h4>
                    <ReasoningFlow graph={report.visualization.graph} />
                  </div>
                ) : report.visualization?.reasoning_html ? (
                  <div className="reasoning-section">
                    <h4>Reasoning Graph</h4>
                    <div
                      className="reasoning-graph"
                      /* Sanitised server-side by html_sanitizer.sanitize_reasoning_html:
                         allow-listed tags/attributes only, no script/style/handlers. */
                      dangerouslySetInnerHTML={{ __html: report.visualization.reasoning_html }}
                    />
                  </div>
                ) : null}

                {/* Per-claim verification results */}
                {verifications.length > 0 && (
                  <div className="claims-section">
                    <h4>Claim Verification</h4>
                    <ul className="claims-list">
                      {verifications.map((item) => (
                        <li key={item.id} className={`claim-item ${statusClass(item.status)}`}>
                          <div className="claim-head">
                            <span className={`claim-status ${statusClass(item.status)}`}>
                              {item.status}
                            </span>
                            <span className={`claim-tier ${item.tier}`}>{item.tier}</span>
                          </div>
                          <p className="claim-text">{item.claim}</p>
                          {item.evidence_quote && (
                            <blockquote className="claim-evidence">
                              {item.evidence_quote}
                              {item.evidence_source && (
                                <cite> — {item.evidence_source}</cite>
                              )}
                            </blockquote>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Enhanced Sources with Links */}
                {report.source_links && report.source_links.length > 0 && (
                  <div className="sources-preview">
                    <h4>Key Sources Referenced:</h4>
                    <ul>
                      {report.source_links.slice(0, 5).map((source, index) => (
                        <li key={index}>
                          <ExternalLink size={14} />
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="source-link"
                          >
                            {source.title || 'News Source'}
                          </a>
                        </li>
                      ))}
                      {report.source_links.length > 5 && (
                        <li className="more-sources">
                          <span>+ {report.source_links.length - 5} more sources (see below)</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                
                {/* Fallback to judge_json.sources if source_links not available */}
                {(!report.source_links || report.source_links.length === 0) && report.judge_json.sources && report.judge_json.sources.length > 0 && (
                  <div className="sources-preview">
                    <h4>Key Sources Referenced:</h4>
                    <ul>
                      {report.judge_json.sources.slice(0, 3).map((source, index) => (
                        <li key={index}>
                          <ExternalLink size={14} />
                          <span>{source}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>

            {/* XTrace memory activity for this run */}
            <MemoryPanel memory={report.memory} />

            <motion.div 
              className="detailed-report glass-effect"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <h3>Detailed Analysis Report</h3>
              <div className="markdown-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  skipHtml={false}
                  allowedElements={undefined}
                  components={markdownComponents}
                >
                  {processMarkdownText(filterVisualizationSummary(report.markdown_report))}
                </ReactMarkdown>
              </div>
            </motion.div>

            {/* Source Links Section */}
            {renderSourceLinks()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportDetail; 